import { useEffect, useRef } from 'react';
import { useBuilderStore } from '../state/builderStore';
import { revealInTracks } from './revealInTracks';

/** Gap (px) kept between the revealed element's top and the canvas viewport's top edge. */
const REVEAL_TOP_GAP = 16;

/**
 * Scroll the editing canvas so the CURRENT selection is in view whenever a reveal is requested — the
 * Shopify behaviour the founder asked for: picking a section/block in the left tree (or adding one) jumps
 * the preview to it, even when you were scrolled far away (e.g. sitting at the footer and selecting the
 * product-main section at the top).
 *
 * Why key on `revealNonce` and NOT on `selectedSectionId`: a click DIRECTLY in the canvas also changes
 * the selection but must NOT reposition the preview — you're already looking at what you clicked. So only
 * the rail / insert / duplicate paths bump `revealNonce`; canvas clicks don't. Keying on the counter also
 * means re-picking the SAME node in the tree re-fires the scroll (a plain `selectedSectionId` effect
 * wouldn't, since the value is unchanged).
 *
 * Container-scoped `scrollBy` (not `Element.scrollIntoView`) so ONLY the preview column (`.bld-center`)
 * moves — never the whole editor document — and so we control the exact top gap and honour reduced-motion.
 */
export function useRevealSelection(): void {
  const revealNonce = useBuilderStore((s) => s.revealNonce);
  const selectedSectionId = useBuilderStore((s) => s.selectedSectionId);
  const selectedBlockId = useBuilderStore((s) => s.selectedBlockId);
  const firstRun = useRef(true);

  useEffect(() => {
    // Skip the initial mount (nonce starts at 0): opening the editor with a restored selection must not
    // yank the canvas around. Only real reveal requests (nonce > 0) scroll.
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }

    // Measure after the next frame so any selection-driven layout (auto-expanded tree, section toolbar)
    // has settled before we read geometry.
    const raf = requestAnimationFrame(() => {
      // Prefer the most-specific selected node: a tree pick can target a nested block. Fall back to the
      // section wrapper the block lives in.
      const el =
        (selectedBlockId &&
          document.querySelector<HTMLElement>(
            `[data-aqx-block-id="${cssEscape(selectedBlockId)}"]`,
          )) ||
        (selectedSectionId &&
          document.querySelector<HTMLElement>(
            `[data-section-id="${cssEscape(selectedSectionId)}"]`,
          )) ||
        null;
      if (!el) return;

      const container = el.closest<HTMLElement>('.bld-center');
      if (!container) return;

      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      /*
        D'ABORD LA PISTE, ENSUITE LA PAGE (fondatrice, 19/09/2026 : « je clique sur une colonne dans le
        rail et c'est une autre qui s'ouvre dans le builder »). Une colonne de Multicolonnes en mode
        carrousel — le cas par défaut sur mobile — vit dans une piste à défilement horizontal. Sans
        cela on faisait défiler l'aperçu jusqu'à la bonne SECTION et on s'arrêtait là : la colonne
        choisie restait hors cadre, et le cadre de sélection se peignait à côté de ce qu'on regarde.
      */
      revealInTracks(el, container, !reduceMotion);

      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      // Distance to move so the element's top sits `REVEAL_TOP_GAP` below the viewport top. Positive =
      // scroll down (element below the fold), negative = scroll up (element above, e.g. footer→top).
      const delta = eRect.top - cRect.top - REVEAL_TOP_GAP;
      // Already aligned → skip (avoids a 0px smooth-scroll flicker when clicking an at-top section).
      if (Math.abs(delta) < 2) return;

      container.scrollBy({ top: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
    // Fire ONLY on an explicit reveal request; the selected ids are read fresh inside but must not
    // re-trigger on their own (a canvas click changes them without wanting a scroll).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealNonce]);
}

/** Defensive attribute-selector escaping — nanoid/section ids are CSS-safe, but never trust raw input. */
function cssEscape(value: string): string {
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(value) : value;
}
